/**
 * File: apps/qbench-cp/reactapp/src/shared/components/FormV2/Select.js
 *
 * Purpose: Renders the plain (non-async) select input used by FormV2 for SELECT and
 *     SELECT_MULTI field types with a fixed option list.
 *
 * Scope: FormV2 input rendering for select and multi-select entity fields backed by a static
 *     `config.options` list.
 *
 * Tags: customer-portal
 *
 * Overview: Exports Select, a memoized component wrapping react-select, bound to
 *     react-hook-form via useController. It converts the raw options array to react-select's
 *     `{ label, value }` shape via toSelectOptions and switches to multi-select mode based on
 *     whether the fieldType is listed in MultiFields. The attached `transform` strategy
 *     converts selected option(s) to plain value(s) (or an array of values for multi-select)
 *     before sending to the server, and passes server values back through toSelectOptions for
 *     display.
 *
 * Exports: default Select component (with an attached `transform` static property).
 */

import isNil from 'lodash/isNil';
import React from 'react';
import { useController } from 'react-hook-form';
import RSelect from 'react-select';
import { MultiFields } from 'shared/constants/fields';
import { toSelectOptions } from 'shared/utils/form';
import { isEmptyString } from 'shared/utils/functions';
import { reactSelectStyle } from './styles';

/**
 * @type {import('./types').FieldTransformStrategy}
 */
const transform = {
  fromServer(value) {
    return value;
  },
  toServer(value, { field }) {
    const { fieldType } = field;
    const isEmpty = isEmptyString(value) || isNil(value);
    const isMulti = MultiFields.includes(fieldType);

    if (isEmpty) return isMulti ? [] : null;
    return isMulti ? value.map((v) => v.value) : value.value;
  },
  defaultValue(value) {
    return toSelectOptions(value);
  },
};

const Select = React.memo(function Select({
  name,
  control,
  required,
  fieldType,
  readOnly,
  config = {},
}) {
  const {
    field: { onChange, value, ref },
  } = useController({ name, control, rules: { required } });
  const options = toSelectOptions(config?.options || []);
  const isMulti = MultiFields.includes(fieldType);
  const customStyles = reactSelectStyle(false, isMulti);

  return (
    <RSelect
      inputId={name}
      inputRef={ref}
      options={options}
      defaultValue={value}
      maxMenuHeight={200}
      styles={customStyles}
      onChange={onChange}
      isDisabled={readOnly}
      isMulti={isMulti}
      isSearchable
      isClearable
    />
  );
});

Select.transform = transform;
export default Select;
